import React, { useState, useMemo, useRef, useEffect } from "react";
import { EditIndexOptionsModal } from "./EditIndexOptionsModal";
import { ChevronsUpDown, Filter, X, Check } from "lucide-react";

const IndexOptionsMultiplierDataTable = ({ indexMultiplierData, indexSearchTerm, handleEditIndexOptionsConfig }) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });

  // Filter-related state
  const [activeFilter, setActiveFilter] = useState(null);
  const [filters, setFilters] = useState({});
  const dropdownRef = useRef(null);

  // Static column definitions
  const columns = [
    { key: 'client_id', label: 'Client ID' },
    { key: 'name', label: 'Name' },
    { key: 'broker', label: 'Broker' },
    { key: 'multiplier', label: 'Multiplier' },
    { key: 'banknifty_multiplier', label: 'BNF Multiplier' },
    { key: 'finnifty_multiplier', label: 'FinNifty Multiplier' },
    { key: 'midcpnifty_multiplier', label: 'MIDCPNifty Multiplier' },
    { key: 'sensex_multiplier', label: 'Sensex Multiplier' },
    { key: 'mcx_multiplier', label: 'MCX Multiplier' }
  ];

  // Get unique values for each column
  const getUniqueValues = (columnKey) => {
    if (!indexMultiplierData || indexMultiplierData.length === 0) return [];
    
    const values = indexMultiplierData.map(row => row[columnKey]);
    return [...new Set(values)].sort((a, b) => {
      if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b);
      }
      return a > b ? 1 : -1;
    });
  };

  // Initialize filters only once
  useEffect(() => {
    if (indexMultiplierData && indexMultiplierData.length > 0 && Object.keys(filters).length === 0) {
      const initialFilters = {};
      columns.forEach(column => {
        const uniqueValues = getUniqueValues(column.key);
        initialFilters[column.key] = uniqueValues.reduce((acc, value) => {
          acc[value] = true; // All values selected by default
          return acc;
        }, {});
      });
      setFilters(initialFilters);
    }
  }, [indexMultiplierData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveFilter(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Step 1: Apply search filter
  const searchFilteredData = useMemo(() => {
    if (!indexMultiplierData || indexMultiplierData.length === 0) {
      return [];
    }

    if (!indexSearchTerm || !indexSearchTerm.trim()) {
      return indexMultiplierData;
    }
    
    const search = indexSearchTerm.toLowerCase().trim();
    return indexMultiplierData.filter(item => 
      item.client_id?.toLowerCase().includes(search) ||
      item.name?.toLowerCase().includes(search)
    );
  }, [indexMultiplierData, indexSearchTerm]);

  // Step 2: Apply column filters
  const columnFilteredData = useMemo(() => {
    if (Object.keys(filters).length === 0) {
      return searchFilteredData;
    }

    return searchFilteredData.filter(row => {
      return columns.every(column => {
        const filterValues = filters[column.key];
        if (!filterValues) return true;
        const cellValue = row[column.key];
        return filterValues[cellValue] === true;
      });
    });
  }, [searchFilteredData, filters]);

  // Step 3: Sort the filtered data
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return columnFilteredData;
    }

    const sortableData = [...columnFilteredData];
    
    sortableData.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

      // Convert to strings for comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      // Try to parse as numbers for numeric sorting
      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);
      
      // If both are valid numbers, sort numerically
      if (!isNaN(aNum) && !isNaN(bNum)) {
        if (sortConfig.direction === 'asc') {
          return aNum - bNum;
        }
        return bNum - aNum;
      }
      
      // Otherwise, sort as strings
      if (sortConfig.direction === 'asc') {
        if (aStr < bStr) return -1;
        if (aStr > bStr) return 1;
        return 0;
      } else {
        if (aStr > bStr) return -1;
        if (aStr < bStr) return 1;
        return 0;
      }
    });

    return sortableData;
  }, [columnFilteredData, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
        setSortConfig({ key: null, direction: null });
        return;
      }
    }
    
    setSortConfig({ key, direction });
  };

  const handleFilterToggle = (columnKey, value) => {
    setFilters(prev => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey],
        [value]: !prev[columnKey][value]
      }
    }));
  };

  const handleSelectAll = (columnKey) => {
    const uniqueValues = getUniqueValues(columnKey);
    setFilters(prev => ({
      ...prev,
      [columnKey]: uniqueValues.reduce((acc, value) => {
        acc[value] = true;
        return acc;
      }, {})
    }));
  };

  const handleClearAll = (columnKey) => {
    const uniqueValues = getUniqueValues(columnKey);
    setFilters(prev => ({
      ...prev,
      [columnKey]: uniqueValues.reduce((acc, value) => {
        acc[value] = true; // Set all to true (show all data)
        return acc;
      }, {})
    }));
  };

  const getSortIcon = (columnKey) => {
    return (
      <ChevronsUpDown className="h-3 w-3 text-white" />
    );
  };

  const getFilterIcon = (columnKey) => {
    const hasActiveFilters = filters[columnKey] && 
      Object.values(filters[columnKey]).some(value => value === false);
    
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setActiveFilter(activeFilter === columnKey ? null : columnKey);
        }}
        className={`p-1 rounded hover:bg-gray-600 transition-colors duration-200 ${
          hasActiveFilters ? 'text-blue-400' : 'text-gray-400'
        }`}
      >
        <Filter className="h-3 w-3" />
      </button>
    );
  };

  const renderFilterDropdown = (columnKey) => {
    if (activeFilter !== columnKey) return null;

    const uniqueValues = getUniqueValues(columnKey);
    const columnFilters = filters[columnKey] || {};
    const selectedCount = Object.values(columnFilters).filter(Boolean).length;
    
    return (
      <div
        ref={dropdownRef}
        className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg z-50 min-w-[200px] max-h-64 overflow-y-auto"
      >
        {/* Header with Select All/Clear All */}
        <div className="p-2 border-b border-gray-600 bg-gray-750">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-gray-300">
              {selectedCount} of {uniqueValues.length} selected
            </span>
            <button
              onClick={() => setActiveFilter(null)}
              className="text-gray-400 hover:text-white"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleSelectAll(columnKey)}
              className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white"
            >
              Select All
            </button>
            <button
              onClick={() => handleClearAll(columnKey)}
              className="text-xs px-2 py-1 bg-gray-600 hover:bg-gray-700 rounded text-white"
            >
              Clear Filter
            </button>
          </div>
        </div>

        {/* Filter Options */}
        <div className="py-1">
          {uniqueValues.map((value) => (
            <label
              key={value}
              className="flex items-center px-3 py-2 hover:bg-gray-700 cursor-pointer text-sm text-white"
            >
              <div className="relative">
                <input
                  type="checkbox"
                  checked={columnFilters[value] || false}
                  onChange={() => handleFilterToggle(columnKey, value)}
                  className="sr-only"
                />
                <div className={`w-4 h-4 border-2 rounded mr-3 flex items-center justify-center ${
                  columnFilters[value] ? 'bg-blue-600 border-blue-600' : 'border-gray-400'
                }`}>
                  {columnFilters[value] && <Check className="h-3 w-3 text-white" />}
                </div>
              </div>
              <span className="truncate">{String(value)}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 h-[calc(100vh-180px)] overflow-y-auto w-full">
      <div className="z-0 w-full h-full animate-reveal transition-all duration-500 ease-in">
        {sortedData.length > 0 ? (
          <table className="bg-black text-sm w-full relative z-0">
            <thead className="bg-[#0F0F0F] sticky top-0 h-[40px] text-sm">
              <tr className="text-white border-b border-gray-800">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="text-left border-r last-of-type:border-r-0 border-gray-800 px-4 py-3 font-semibold hover:bg-gray-800 transition-colors duration-200 select-none relative"
                  >
                    <div className="flex items-center justify-between">
                      <span>{column.label}</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleSort(column.key)}
                          className="p-1 rounded hover:bg-gray-600 transition-colors duration-200"
                        >
                          {getSortIcon(column.key)}
                        </button>
                        {getFilterIcon(column.key)}
                      </div>
                    </div>
                    {renderFilterDropdown(column.key)}
                  </th>
                ))}
                <th className="text-center border-r last-of-type:border-r-0 border-gray-800 px-4 py-3 font-semibold">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item, index) => (
                <tr
                  key={`${item.client_id}-${index}`}
                  className="bg-black z-0 border-b last-of-type:border-b-0 border-gray-900 font-medium text-white hover:bg-black/90"
                >
                  <td className="px-4 py-2 border-r border-gray-900">{item.client_id}</td>
                  <td className="px-4 py-2 border-r border-gray-900">{item.name}</td>
                  <td className="px-4 py-2 border-r border-gray-900">{item.broker}</td>
                  <td className={`px-4 py-2 border-r border-gray-900 ${item.multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.multiplier}</td>
                  <td className={`px-4 py-2 border-r border-gray-900 ${item.banknifty_multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.banknifty_multiplier}</td>
                  <td className={`px-4 py-2 border-r border-gray-900 ${item.finnifty_multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.finnifty_multiplier}</td>
                  <td className={`px-4 py-2 border-r border-gray-900 ${item.midcpnifty_multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.midcpnifty_multiplier}</td>
                  <td className={`px-4 py-2 border-r border-gray-900 ${item.sensex_multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.sensex_multiplier}</td>
                  <td className={`px-4 py-2 border-r border-gray-900 ${item.mcx_multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.mcx_multiplier}</td>
                  <td className="px-4 text-center py-2">
                    <EditIndexOptionsModal
                      clientId={item.client_id}
                      broker={item.broker}
                      prevMultiplier={item.multiplier}
                      prevBankNiftyMultiplier={item.banknifty_multiplier}
                      prevFinNiftyMultiplier={item.finnifty_multiplier}
                      prevMidcpNiftyMultiplier={item.midcpnifty_multiplier}
                      prevSensexMultiplier={item.sensex_multiplier}
                      prevMcxMultiplier={item.mcx_multiplier}
                      handleEditIndexOptionsConfig={handleEditIndexOptionsConfig}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <h3 className="flex items-center text-gray-300 text-md font-medium uppercase">
            No Data Available
          </h3>
        )}
        
        {/* Results Summary */}
        <div className="mt-4 px-6 text-sm text-gray-400">
          Showing {sortedData.length} of {indexMultiplierData?.length || 0} records
          {indexSearchTerm && ` (filtered by: "${indexSearchTerm}")`}
        </div>
      </div>
    </div>
  );
};

export default IndexOptionsMultiplierDataTable;



























































// import React, { useState, useMemo, useRef, useEffect } from "react";
// import { EditIndexOptionsModal } from "./EditIndexOptionsModal";
// import { ChevronsUpDownIcon, Filter, ChevronDown, Check, X } from "lucide-react";

// const IndexOptionsMuliplierDataTable = ({ indexMultiplierData, indexSearchTerm, handleEditIndexOptionsConfig }) => {
//   const [sortConfig, setSortConfig] = useState({
//     key: null,
//     direction: null
//   });

//   // Filter-related state
//   const [filterMode, setFilterMode] = useState(false);
//   const [activeFilter, setActiveFilter] = useState(null);
//   const [filters, setFilters] = useState({});
//   const dropdownRef = useRef(null);

//   // Static column definitions
//   const columns = [
//     { key: 'client_id', label: 'Client ID' },
//     { key: 'name', label: 'Name' },
//     { key: 'broker', label: 'Broker' },
//     { key: 'multiplier', label: 'Multiplier' },
//     { key: 'banknifty_multiplier', label: 'BNF Multiplier' },
//     { key: 'finnifty_multiplier', label: 'FinNifty Multiplier' },
//     { key: 'midcpnifty_multiplier', label: 'MIDCPNifty Multiplier' },
//     { key: 'sensex_multiplier', label: 'Sensex Multiplier' },
//     { key: 'mcx_multiplier', label: 'MCX Multiplier' }
//   ];

//   // Get unique values for each column
//   const getUniqueValues = (columnKey) => {
//     if (!indexMultiplierData || indexMultiplierData.length === 0) return [];
    
//     const values = indexMultiplierData.map(row => row[columnKey]);
//     return [...new Set(values)].sort((a, b) => {
//       if (typeof a === 'string' && typeof b === 'string') {
//         return a.localeCompare(b);
//       }
//       return a > b ? 1 : -1;
//     });
//   };

//   // Initialize filters only once
//   useEffect(() => {
//     if (indexMultiplierData && indexMultiplierData.length > 0 && Object.keys(filters).length === 0) {
//       const initialFilters = {};
//       columns.forEach(column => {
//         const uniqueValues = getUniqueValues(column.key);
//         initialFilters[column.key] = uniqueValues.reduce((acc, value) => {
//           acc[value] = true; // All values selected by default
//           return acc;
//         }, {});
//       });
//       setFilters(initialFilters);
//     }
//   }, [indexMultiplierData]);

//   // Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setActiveFilter(null);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, []);

//   // Step 1: Apply search filter
//   const searchFilteredData = useMemo(() => {
//     if (!indexMultiplierData || indexMultiplierData.length === 0) {
//       return [];
//     }

//     if (!indexSearchTerm || !indexSearchTerm.trim()) {
//       return indexMultiplierData;
//     }
    
//     const search = indexSearchTerm.toLowerCase().trim();
//     return indexMultiplierData.filter(item => 
//       item.client_id?.toLowerCase().includes(search) ||
//       item.name?.toLowerCase().includes(search)
//     );
//   }, [indexMultiplierData, indexSearchTerm]);

//   // Step 2: Apply column filters
//   const columnFilteredData = useMemo(() => {
//     if (Object.keys(filters).length === 0) {
//       return searchFilteredData;
//     }

//     return searchFilteredData.filter(row => {
//       return columns.every(column => {
//         const filterValues = filters[column.key];
//         if (!filterValues) return true;
//         const cellValue = row[column.key];
//         return filterValues[cellValue] === true;
//       });
//     });
//   }, [searchFilteredData, filters]);

//   // Step 3: Sort the filtered data
//   const sortedData = useMemo(() => {
//     if (!sortConfig.key || !sortConfig.direction) {
//       return columnFilteredData;
//     }

//     const sortableData = [...columnFilteredData];
    
//     sortableData.sort((a, b) => {
//       const aValue = a[sortConfig.key];
//       const bValue = b[sortConfig.key];

//       // Handle null/undefined values
//       if (aValue == null && bValue == null) return 0;
//       if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
//       if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

//       // Convert to strings for comparison
//       const aStr = String(aValue).toLowerCase();
//       const bStr = String(bValue).toLowerCase();
      
//       // Try to parse as numbers for numeric sorting
//       const aNum = parseFloat(aValue);
//       const bNum = parseFloat(bValue);
      
//       // If both are valid numbers, sort numerically
//       if (!isNaN(aNum) && !isNaN(bNum)) {
//         if (sortConfig.direction === 'asc') {
//           return aNum - bNum;
//         }
//         return bNum - aNum;
//       }
      
//       // Otherwise, sort as strings
//       if (sortConfig.direction === 'asc') {
//         if (aStr < bStr) return -1;
//         if (aStr > bStr) return 1;
//         return 0;
//       } else {
//         if (aStr > bStr) return -1;
//         if (aStr < bStr) return 1;
//         return 0;
//       }
//     });

//     return sortableData;
//   }, [columnFilteredData, sortConfig]);

//   const handleSort = (key) => {
//     let direction = 'asc';
    
//     if (sortConfig.key === key) {
//       if (sortConfig.direction === 'asc') {
//         direction = 'desc';
//       } else if (sortConfig.direction === 'desc') {
//         direction = null;
//         setSortConfig({ key: null, direction: null });
//         return;
//       }
//     }
    
//     setSortConfig({ key, direction });
//   };

//   const handleFilterToggle = (columnKey, value) => {
//     setFilters(prev => ({
//       ...prev,
//       [columnKey]: {
//         ...prev[columnKey],
//         [value]: !prev[columnKey][value]
//       }
//     }));
//   };

//   const handleSelectAll = (columnKey) => {
//     const uniqueValues = getUniqueValues(columnKey);
//     setFilters(prev => ({
//       ...prev,
//       [columnKey]: uniqueValues.reduce((acc, value) => {
//         acc[value] = true;
//         return acc;
//       }, {})
//     }));
//   };

//   const handleSelectNone = (columnKey) => {
//     const uniqueValues = getUniqueValues(columnKey);
//     setFilters(prev => ({
//       ...prev,
//       [columnKey]: uniqueValues.reduce((acc, value) => {
//         acc[value] = false;
//         return acc;
//       }, {})
//     }));
//   };

//   const getSortIcon = (columnKey) => {
//     return (
//       <span className="inline-block ml-1 text-white">
//         <ChevronsUpDownIcon className="h-3 w-3 text-white" />
//       </span>
//     );
//   };

//   const getFilterIcon = (columnKey) => {
//     if (!filterMode) return null;
    
//     const hasActiveFilters = filters[columnKey] && 
//       Object.values(filters[columnKey]).some(value => value === false);
    
//     return (
//       <button
//         onClick={(e) => {
//           e.stopPropagation();
//           setActiveFilter(activeFilter === columnKey ? null : columnKey);
//         }}
//         className={`ml-2 p-1 rounded hover:bg-gray-600 transition-colors duration-200 ${
//           hasActiveFilters ? 'text-blue-400' : 'text-gray-400'
//         }`}
//       >
//         <ChevronDown className="h-3 w-3" />
//       </button>
//     );
//   };

//   const renderFilterDropdown = (columnKey) => {
//     if (activeFilter !== columnKey) return null;

//     const uniqueValues = getUniqueValues(columnKey);
//     const columnFilters = filters[columnKey] || {};
//     const selectedCount = Object.values(columnFilters).filter(Boolean).length;
    
//     return (
//       <div
//         ref={dropdownRef}
//         className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg z-50 min-w-[200px] max-h-64 overflow-y-auto"
//       >
//         {/* Header with Select All/None */}
//         <div className="p-2 border-b border-gray-600 bg-gray-750">
//           <div className="flex justify-between items-center mb-2">
//             <span className="text-xs text-gray-300">
//               {selectedCount} of {uniqueValues.length} selected
//             </span>
//             <button
//               onClick={() => setActiveFilter(null)}
//               className="text-gray-400 hover:text-white"
//             >
//               <X className="h-3 w-3" />
//             </button>
//           </div>
//           <div className="flex gap-2">
//             <button
//               onClick={() => handleSelectAll(columnKey)}
//               className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white"
//             >
//               Select All
//             </button>
//             <button
//               onClick={() => handleSelectNone(columnKey)}
//               className="text-xs px-2 py-1 bg-gray-600 hover:bg-gray-700 rounded text-white"
//             >
//               Clear All
//             </button>
//           </div>
//         </div>

//         {/* Filter Options */}
//         <div className="py-1">
//           {uniqueValues.map((value) => (
//             <label
//               key={value}
//               className="flex items-center px-3 py-2 hover:bg-gray-700 cursor-pointer text-sm text-white"
//             >
//               <div className="relative">
//                 <input
//                   type="checkbox"
//                   checked={columnFilters[value] || false}
//                   onChange={() => handleFilterToggle(columnKey, value)}
//                   className="sr-only"
//                 />
//                 <div className={`w-4 h-4 border-2 rounded mr-3 flex items-center justify-center ${
//                   columnFilters[value] ? 'bg-blue-600 border-blue-600' : 'border-gray-400'
//                 }`}>
//                   {columnFilters[value] && <Check className="h-3 w-3 text-white" />}
//                 </div>
//               </div>
//               <span className="truncate">{String(value)}</span>
//             </label>
//           ))}
//         </div>
//       </div>
//     );
//   };

//   return (
//     <div className="grid grid-cols-1 h-[calc(100vh-180px)] overflow-y-auto w-full">
//       <div className="z-0 w-full h-full animate-reveal transition-all duration-500 ease-in">
//         {/* Filter Toggle Button */}
//         <div className="mb-4 flex justify-end px-6">
//           <button
//             onClick={() => {
//               setFilterMode(!filterMode);
//               setActiveFilter(null);
//             }}
//             className={`flex items-center gap-2 px-4 py-2 rounded-md border transition-colors duration-200 ${
//               filterMode 
//                 ? 'bg-blue-600 border-blue-600 text-white' 
//                 : 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700'
//             }`}
//           >
//             <Filter className="h-4 w-4" />
//             {filterMode ? 'Hide Filters' : 'Show Filters'}
//           </button>
//         </div>

//         {sortedData.length > 0 ? (
//           <table className="bg-black  text-sm w-full relative z-0">
//             <thead className="bg-[#0F0F0F] sticky top-0 h-[40px] text-sm">
//               <tr className="text-white border-b border-gray-800">
//                 {columns.map((column) => (
//                   <th
//                     key={column.key}
//                     className="text-left border-r last-of-type:border-r-0 border-gray-800 px-4 py-3 font-semibold cursor-pointer hover:bg-gray-800 transition-colors duration-200 select-none relative"
//                     onClick={() => handleSort(column.key)}
//                   >
//                     <div className="flex items-center justify-between">
//                       <span>{column.label}</span>
//                       <div className="flex items-center">
//                         {getSortIcon(column.key)}
//                         {getFilterIcon(column.key)}
//                       </div>
//                     </div>
//                     {renderFilterDropdown(column.key)}
//                   </th>
//                 ))}
//                 <th className="text-center border-r last-of-type:border-r-0 border-gray-800 px-4 py-3 font-semibold">
//                   Action
//                 </th>
//               </tr>
//             </thead>
//             <tbody>
//               {sortedData.map((item, index) => (
//                 <tr
//                   key={`${item.client_id}-${index}`}
//                   className="bg-black z-0 border-b last-of-type:border-b-0 border-gray-900 font-medium text-white hover:bg-black/90"
//                 >
//                   <td className="px-4 py-2 border-r border-gray-900">{item.client_id}</td>
//                   <td className="px-4 py-2 border-r border-gray-900">{item.name}</td>
//                   <td className="px-4 py-2 border-r border-gray-900">{item.broker}</td>
//                   <td className={`px-4 py-2 border-r border-gray-900 ${item.multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.multiplier}</td>
//                   <td className={`px-4 py-2 border-r border-gray-900 ${item.banknifty_multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.banknifty_multiplier}</td>
//                   <td className={`px-4 py-2 border-r border-gray-900 ${item.finnifty_multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.finnifty_multiplier}</td>
//                   <td className={`px-4 py-2 border-r border-gray-900 ${item.midcpnifty_multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.midcpnifty_multiplier}</td>
//                   <td className={`px-4 py-2 border-r border-gray-900 ${item.sensex_multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.sensex_multiplier}</td>
//                   <td className={`px-4 py-2 border-r border-gray-900 ${item.mcx_multiplier >= 1 ? 'bg-emerald-600 text-white font-semibold' : ''}`}>{item.mcx_multiplier}</td>
//                   <td className="px-4 text-center py-2">
//                     <EditIndexOptionsModal
//                       clientId={item.client_id}
//                       broker={item.broker}
//                       prevMultiplier={item.multiplier}
//                       prevBankNiftyMultiplier={item.banknifty_multiplier}
//                       prevFinNiftyMultiplier={item.finnifty_multiplier}
//                       prevMidcpNiftyMultiplier={item.midcpnifty_multiplier}
//                       prevSensexMultiplier={item.sensex_multiplier}
//                       prevMcxMultiplier={item.mcx_multiplier}
//                       handleEditIndexOptionsConfig={handleEditIndexOptionsConfig}
//                     />
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         ) : (
//           <h3 className="flex items-center text-gray-300 text-md font-medium uppercase">
//             No Data Available
//           </h3>
//         )}
        
//         {/* Results Summary */}
//         <div className="mt-4 text-sm text-gray-400">
//           Showing {sortedData.length} of {indexMultiplierData?.length || 0} records
//           {indexSearchTerm && ` (filtered by: "${indexSearchTerm}")`}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default IndexOptionsMuliplierDataTable;





































